import { Box } from '@mui/material';
import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import api from '../../../../utils/api';
import { Option } from '../../../common/AsyncAutocomplete';
import Button from '../../../common/Button';
import Loading from '../../../common/Loading';
import VacancyBasicCard, { BasicEntry } from './VacancyBasicCard';
import VacancyCustomerCard from './VacancyCustomerCard';
import VacancySkillCard from './VacancySkillCard';

interface AddVacancyFormProps {
  type: 'add' | 'edit' | 'view';
  onCancel: () => void;
  onSave: () => void;
  setAddedVacancyId?: (id: number) => void;
}

export interface CustomerEntry {
  id: string;
  customerName: string;
  customerCountry: string;
  numberOfVacancies: string;
}

export interface SkillEntry {
  id: string;
  skill: Option | null;
  years: string;
}

export interface QualificationEntry {
  id: string;
  qualification: Option | null;
  type: string;
}

export interface TechnologyEntry {
  id: string;
  technology: Option | null;
  years: string;
}

export interface VacancyApiResponse {
  id: number;
  message?: string;
  vacancyType: string;
  vacancyTitle: string;
  vacancyCode: string;
  minimumIndustryExperience: number;
  vacancyStartDate: string;
  vacancyEndDate: string;
  vacancyReason: string;
  designation: {
    id: number;
    designation: string;
  } | null;
  isActive: boolean;
  jobDescriptionAI: string;
  jobDescription: string;
  description: string;
  city: {
    id: number;
    name: string;
  } | null;
  country: {
    id: number;
    name: string;
  } | null;
  vacancyAboutClient: string;
  vacancyAboutEyepax: string;
  jdInProgress?: boolean;
  vacancyUrl: string;
  vacancyTinyUrl: string;
  socialMediaDesc: string;
  socialMediaTags: string;
  imageLinkedIn: File | null;
  imageFB: File | null;
  customers: Array<{
    name: string;
    country: string;
    numberOfVacancies: number;
  }>;
  qualifications: Array<{
    id: number;
    name: string;
    qualificationType: string;
  }>;
  primarySkills: Array<{
    id: number;
    name: string;
    yearsOfExperience: number;
  }>;
  secondarySkills: Array<{
    id: number;
    name: string;
    yearsOfExperience: number;
  }>;
  primaryTechnologies: Array<{
    id: number;
    name: string;
    yearsOfExperience: number;
  }>;
  secondaryTechnologies: Array<{
    id: number;
    name: string;
    yearsOfExperience: number;
  }>;
}

const VacancyForm: React.FC<AddVacancyFormProps> = ({ type, onCancel, onSave, setAddedVacancyId }) => {
  const params = useParams();
  const [isSaving, setIsSaving] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [basicData, setBasicData] = useState<BasicEntry>({
    vacancyType: '',
    vacancyTitle: '',
    vacancyCode: '',
    minIndustryExperience: '',
    startDate: '',
    endDate: '',
    vacancyReason: '',
    designation: null,
    isActive: true
  });
  const [customers, setCustomers] = useState<CustomerEntry[]>([{ id: '1', customerName: '', customerCountry: '', numberOfVacancies: '' }]);
  const [qualifications, setQualifications] = useState<QualificationEntry[]>([{ id: '1', qualification: null, type: '' }]);
  const [primarySkills, setPrimarySkills] = useState<SkillEntry[]>([{ id: '1', skill: null, years: '' }]);
  const [secondarySkills, setSecondarySkills] = useState<SkillEntry[]>([{ id: '1', skill: null, years: '' }]);
  const [primaryTechnologies, setPrimaryTechnologies] = useState<TechnologyEntry[]>([{ id: '1', technology: null, years: '' }]);
  const [secondaryTechnologies, setSecondaryTechnologies] = useState<TechnologyEntry[]>([{ id: '1', technology: null, years: '' }]);

  useEffect(() => {
    const vacancyId = params?.id;

    if (type === 'add' || vacancyId === undefined || vacancyId === null) return;

    setIsLoading(true);
    api.get<VacancyApiResponse>(
      `/rec/vacancy/${vacancyId}`,
      {
        headers: {
          'x-api-id': 11,
          'x-page-id': type === 'view' ? 13 : 10,
        },
      }
    ).then((response) => {
      const data = response.data;

      // Map basic data
      setBasicData({
        vacancyType: data.vacancyType || '',
        vacancyTitle: data.vacancyTitle || '',
        vacancyCode: data.vacancyCode || '',
        minIndustryExperience: data.minimumIndustryExperience?.toString() || '',
        startDate: data.vacancyStartDate || '',
        endDate: data.vacancyEndDate || '',
        vacancyReason: data.vacancyReason || '',
        designation: data.designation ? { value: data.designation.id.toString(), label: data.designation.designation } : null,
        isActive: data.isActive
      });

      // Map customers
      if (data.customers && data.customers.length > 0) {
        setCustomers(data.customers.map((customer, index) => ({
          id: (index + 1).toString(),
          customerName: customer.name || '',
          customerCountry: customer.country || '',
          numberOfVacancies: customer.numberOfVacancies?.toString() || ''
        })));
      }

      // Map qualifications
      if (data.qualifications && data.qualifications.length > 0) {
        setQualifications(data.qualifications.map((qual, index) => ({
          id: (index + 1).toString(),
          qualification: qual.id ? { value: qual.id.toString(), label: qual.name } : null,
          type: qual.qualificationType || ''
        })));
      }

      // Map primary skills
      if (data.primarySkills && data.primarySkills.length > 0) {
        setPrimarySkills(data.primarySkills.map((skill, index) => ({
          id: (index + 1).toString(),
          skill: skill.id ? { value: skill.id.toString(), label: skill.name } : null,
          years: skill.yearsOfExperience?.toString() || ''
        })));
      }

      // Map secondary skills
      if (data.secondarySkills && data.secondarySkills.length > 0) {
        setSecondarySkills(data.secondarySkills.map((skill, index) => ({
          id: (index + 1).toString(),
          skill: skill.id ? { value: skill.id.toString(), label: skill.name } : null,
          years: skill.yearsOfExperience?.toString() || ''
        })));
      }

      // Map primary technologies
      if (data.primaryTechnologies && data.primaryTechnologies.length > 0) {
        setPrimaryTechnologies(data.primaryTechnologies.map((tech, index) => ({
          id: (index + 1).toString(),
          technology: tech.id ? { value: tech.id.toString(), label: tech.name } : null,
          years: tech.yearsOfExperience?.toString() || ''
        })));
      }

      // Map secondary technologies
      if (data.secondaryTechnologies && data.secondaryTechnologies.length > 0) {
        setSecondaryTechnologies(data.secondaryTechnologies.map((tech, index) => ({
          id: (index + 1).toString(),
          technology: tech.id ? { value: tech.id.toString(), label: tech.name } : null,
          years: tech.yearsOfExperience?.toString() || ''
        })));
      }
      setIsLoading(false);
    });
  }, [type, params.id]);

  const handleSave = () => {
    if (type === 'add') {
      handleAddVacancySave();
    } else {
      handleEditVacancySave();
    }
  };

  const handleEditVacancySave = async () => {
    try {
      setIsSaving(true);
      const vacancyId = params.id;
      if (!vacancyId) return;

      const formData = new FormData();

      // Add basic data
      formData.append('vacancyType', basicData.vacancyType);
      formData.append('vacancyTitle', basicData.vacancyTitle);
      formData.append('vacancyCode', basicData.vacancyCode);
      formData.append('minimumIndustryExperience', basicData.minIndustryExperience);
      formData.append('vacancyStartDate', basicData.startDate || '');
      formData.append('vacancyEndDate', basicData.endDate || '');
      formData.append('vacancyReason', basicData.vacancyReason);
      formData.append('designation', basicData.designation?.value || '');
      formData.append('isActive', basicData.isActive.toString());

      // Add customer data
      const customersData: any[] = [];
      customers.forEach((customer, index) => {
        if (customer.customerName && customer.customerCountry && customer.numberOfVacancies) {
          customersData.push({
            name: customer.customerName,
            country: customer.customerCountry,
            numberOfVacancies: customer.numberOfVacancies
          });
        }
      });
      formData.append('customers', JSON.stringify(customersData));

      // Add qualification data
      const qualificationsData: any[] = [];
      qualifications.forEach((qualification, index) => {
        if (qualification.qualification && qualification.type) {
          qualificationsData.push({
            id: qualification.qualification?.value,
          });
        }
      });
      formData.append('qualifications', JSON.stringify(qualificationsData));

      // Add primary skills data
      const primarySkillsData: any[] = [];
      primarySkills.forEach((skill, index) => {
        if (skill.skill && skill.years) {
          primarySkillsData.push({
            id: skill.skill?.value,
            yearsOfExperience: skill.years
          });
        }
      });
      formData.append('primarySkills', JSON.stringify(primarySkillsData));

      // Add secondary skills data
      const secondarySkillsData: any[] = [];
      secondarySkills.forEach((skill, index) => {
        if (skill.skill && skill.years) {
          secondarySkillsData.push({
            id: skill.skill?.value,
            yearsOfExperience: skill.years
          });
        }
      });
      formData.append('secondarySkills', JSON.stringify(secondarySkillsData));

      // Add primary technologies data
      const primaryTechnologiesData: any[] = [];
      primaryTechnologies.forEach((technology, index) => {
        if (technology.technology && technology.years) {
          primaryTechnologiesData.push({
            id: technology.technology?.value,
            yearsOfExperience: technology.years
          });
        }
      });
      formData.append('primaryTechnologies', JSON.stringify(primaryTechnologiesData));

      // Add secondary technologies data
      const secondaryTechnologiesData: any[] = [];
      secondaryTechnologies.forEach((technology, index) => {
        if (technology.technology && technology.years) {
          secondaryTechnologiesData.push({
            id: technology.technology?.value,
            yearsOfExperience: technology.years
          });
        }
      });
      formData.append('secondaryTechnologies', JSON.stringify(secondaryTechnologiesData));

      const response = await api.put<VacancyApiResponse>(
        `/rec/vacancy/${vacancyId}/updateDetails`,
        formData,
        {
          headers: {
            'x-api-id': 12,
            'x-page-id': 13,
            'Content-Type': 'multipart/form-data',
          },
        }
      );

      if (response.status === 200) {
        onSave?.();
      }
    } catch (error) {
      console.error('Error updating vacancy:', error);
    } finally {
      setIsSaving(false);
    }
  };

  const handleAddVacancySave = async () => {
    try {
      setIsSaving(true);

      const formData: any = {};

      // Add basic data
      formData.vacancyType = basicData.vacancyType;
      formData.vacancyTitle = basicData.vacancyTitle;
      formData.vacancyCode = basicData.vacancyCode;
      formData.minIndustryExperience = parseInt(basicData.minIndustryExperience);
      formData.vacancyStartDate = basicData.startDate || '';
      formData.vacancyEndDate = basicData.endDate || '';
      formData.vacancyReason = basicData.vacancyReason;
      formData.designation = parseInt(basicData.designation?.value || '');
      formData.isActive = basicData.isActive;


      // Add customer data
      formData.customers = [];
      customers.forEach((customer, index) => {
        if (customer.customerName && customer.customerCountry && customer.numberOfVacancies) {
          formData.customers[index] = {};
          formData.customers[index].name = customer.customerName;
          formData.customers[index].country = customer.customerCountry;
          formData.customers[index].numberOfVacancies = parseInt(customer.numberOfVacancies);
        }
      });

      // Add qualification data
      formData.qualifications = [];
      qualifications.forEach((qualification, index) => {
        if (qualification.qualification && qualification.type) {
          formData.qualifications[index] = {};
          formData.qualifications[index].qualificationID = parseInt(qualification.qualification?.value || '');
          formData.qualifications[index].type = qualification.type;
        }
      });

      // Add primary skills data
      formData.primarySkills = [];
      primarySkills.forEach((skill, index) => {
        if (skill.skill && skill.years) {
          formData.primarySkills[index] = {};
          formData.primarySkills[index].skillID = parseInt(skill.skill?.value || '');
          formData.primarySkills[index].yearsOfExperience = parseInt(skill.years);
        }
      });

      // Add secondary skills data
      formData.secondarySkills = [];
      secondarySkills.forEach((skill, index) => {
        if (skill.skill && skill.years) {
          formData.secondarySkills[index] = {};
          formData.secondarySkills[index].skillID = parseInt(skill.skill?.value || '');
          formData.secondarySkills[index].yearsOfExperience = parseInt(skill.years);
        }
      });

      // Add primary technologies data
      formData.primaryTechnologies = [];
      primaryTechnologies.forEach((technology, index) => {
        if (technology.technology && technology.years) {
          formData.primaryTechnologies[index] = {};
          formData.primaryTechnologies[index].technologyID = parseInt(technology.technology?.value || '');
          formData.primaryTechnologies[index].yearsOfExperience = parseInt(technology.years);
        }
      });

      // Add secondary technologies data
      formData.secondaryTechnologies = [];
      secondaryTechnologies.forEach((technology, index) => {
        if (technology.technology && technology.years) {
          formData.secondaryTechnologies[index] = {};
          formData.secondaryTechnologies[index].technologyID = parseInt(technology.technology?.value || '');
          formData.secondaryTechnologies[index].yearsOfExperience = parseInt(technology.years);
        }
      });

      const response = await api.post<VacancyApiResponse>('/rec/vacancy/add', formData, {
        headers: {
          'x-api-id': 10,
          'x-page-id': 7,
        },
      });

      if (response.status === 201) {
        if (setAddedVacancyId) {
          setAddedVacancyId(response.data.id);
        }
        onSave?.();
      }
    } catch (error) {
      console.error('Error saving vacancy:', error);
    } finally {
      setIsSaving(false);
    }
  };

  const handleAddCustomer = () => {
    setCustomers([...customers, { id: Date.now().toString(), customerName: '', customerCountry: '', numberOfVacancies: '' }]);
  };

  const handleRemoveCustomer = (id: string) => {
    setCustomers(customers.filter(customer => customer.id !== id));
  };

  return (
    <Box gap={3} display="flex" flexDirection="column">
      {isLoading ? <Loading /> : (
        <>
          {/* Basic Information */}
          <VacancyBasicCard
            type={type}
            basicData={basicData}
            setBasicData={setBasicData}
          />

          {/* Customer Information */}
          <VacancyCustomerCard
            type={type}
            customers={customers}
            onAddCustomer={handleAddCustomer}
            onRemoveCustomer={handleRemoveCustomer}
            setCustomers={setCustomers}
          />

          {/* Skills and Technologies */}
          <VacancySkillCard
            type={type}
            qualifications={qualifications}
            primarySkills={primarySkills}
            secondarySkills={secondarySkills}
            primaryTechnologies={primaryTechnologies}
            secondaryTechnologies={secondaryTechnologies}
            setQualifications={setQualifications}
            setPrimarySkills={setPrimarySkills}
            setSecondarySkills={setSecondarySkills}
            setPrimaryTechnologies={setPrimaryTechnologies}
            setSecondaryTechnologies={setSecondaryTechnologies}
          />

          {/* Action Buttons */}
          {type !== 'view' && (
            <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 2, width: '1502px' }}>
              <Button
                variant="outlined"
                onClick={onCancel}
                className="edit-section-cancel-button"
                disabled={isSaving}
              >
                Cancel
              </Button>
              <Button
                variant="contained"
                onClick={handleSave}
                className="edit-section-save-button"
                disabled={isSaving}
              >
                {isSaving ? 'Saving...' : 'Save'}
              </Button>
            </Box>
          )}
        </>
      )}
    </Box>
  );
};

export default VacancyForm; 